import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Suggester from 'src/comps/Molecules/Suggester'
import { updateLastSuggestion, updateSearchAction } from 'src/effects/Search/actions'
import {
  fetchSuggestionsRequestAction,
  resetSuggestionsAction,
} from 'src/effects/Suggester/actions'
import {
  getSuggesterIsFetching,
  getSuggesterIsFirstSearch,
  getSuggesterSearch,
  getSuggestions,
} from 'src/effects/Suggester/selectors'
import { useMarket } from 'src/hooks/useMarket'
import type { SearchMode, SuggestionType } from 'src/types/search'

interface ISuggesterProps {
  readonly mode: SearchMode
  readonly initialValue?: SuggestionType
  readonly handleOnSelect?: (search: SuggestionType | null) => void
  readonly onInput?: () => void
  readonly className?: string
  readonly error?: boolean
}

interface ISuggestionFromApi {
  id: string
  coordinates: { lat: number; lon: number }
  country: string
  description: string
  slug: string
  stars: number
  type: string
  text: string
  citySlug: string
  boxShape?: {
    BottomRight: {
      Lat: number
      Lon: number
    }
    TopLeft: {
      Lat: number
      Lon: number
    }
  }
}

const suggestionsMapping = (suggestion: ISuggestionFromApi) => ({
  ID: suggestion.id,
  Coordinate: suggestion.coordinates,
  Country: suggestion.country,
  Slug: suggestion.slug,
  Type: suggestion.type,
  Text: suggestion.text,
  Description: suggestion.description,
  Stars: suggestion.stars,
  CitySlug: suggestion.citySlug,
  ...(suggestion.boxShape && {
    BoxShape: {
      topLeft: {
        lat: suggestion.boxShape.TopLeft.Lat,
        lon: suggestion.boxShape.TopLeft.Lon,
      },
      bottomRight: {
        lat: suggestion.boxShape.BottomRight.Lat,
        lon: suggestion.boxShape.BottomRight.Lon,
      },
    },
  }),
})

function SuggesterConnected({
  handleOnSelect,
  mode,
  initialValue,
  className,
  error,
  onInput,
}: ISuggesterProps) {
  const dispatch = useDispatch()
  const { market, language } = useMarket()

  const [suggestion, setSuggestion] = useState<SuggestionType | undefined>(initialValue)

  useEffect(() => {
    setSuggestion(initialValue)
  }, [initialValue])

  const suggestions = useSelector<unknown, ISuggestionFromApi[]>(getSuggestions)

  const suggestionMapper = suggestions.map(suggestionsMapping)

  const suggesterSearch = useSelector(getSuggesterSearch)

  const isFirstSearch = useSelector(getSuggesterIsFirstSearch)
  const isFetchingSuggestions = useSelector(getSuggesterIsFetching)

  function onChangeInput(search: string | undefined) {
    if (!search) {
      dispatch(resetSuggestionsAction())
    }

    if (search && search.length > 1) {
      dispatch(
        fetchSuggestionsRequestAction({
          language,
          market,
          search,
          variant: `WEB-4624.1`,
        })
      )
    }
    onInput?.()
  }

  const onSelect = (value: SuggestionType | null) => {
    if (!value) {
      dispatch(resetSuggestionsAction())
    }

    setSuggestion(value ?? undefined)

    dispatch(
      updateSearchAction({
        city: value?.CitySlug,
        country: value?.Country,
        latitude: value?.Coordinate.lat,
        longitude: value?.Coordinate.lon,
        boxShape: value?.BoxShape,
        slug: value?.Slug,
        type: value?.Type,
        term: [value?.Text, value?.Description].filter(Boolean).join(', '),
        description: value?.Description,
        id: value?.ID,
        text: value?.Text,
      })
    )

    dispatch(
      updateLastSuggestion({
        citySlug: value?.CitySlug,
        coordinates: {
          lat: value?.Coordinate.lat,
          lon: value?.Coordinate.lon,
        },
        boxShape: value?.BoxShape,
        country: value?.Country,
        description: value?.Description,
        id: value?.ID,
        slug: value?.Slug,
        stars: value?.Stars,
        term: [value?.Text, value?.Description].filter(Boolean).join(', '),
        text: value?.Text,
        type: value?.Type,
      })
    )

    handleOnSelect?.(value)
  }

  return (
    <Suggester
      className={className}
      onChangeInput={onChangeInput}
      suggestions={suggestionMapper}
      suggesterSearch={suggesterSearch}
      isFetchingSuggestions={isFetchingSuggestions}
      onSelect={onSelect}
      isFirstSearch={isFirstSearch}
      suggestion={suggestion}
      mode={mode}
      hasError={error && !suggestion?.Slug}
    />
  )
}

export default SuggesterConnected
